import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import SendEmailModal from './components/Admin/SendEmailModal';
import { useLanguage } from './contexts/LanguageContext';

export default function Admin() {
	const { currentUser, logout } = useAuth();
	const navigate = useNavigate();
	const [people, setPeople] = useState([]);
	const [selectedPerson, setSelectedPerson] = useState(null);
	const [resolutions, setResolutions] = useState([]);
	const [name, setName] = useState('');
	const [surname, setSurname] = useState('');
	const [resolutionText, setResolutionText] = useState('');
	const [resolutionCount, setResolutionCount] = useState('');
	const [resolutionYear, setResolutionYear] = useState(new Date().getFullYear());
	const [message, setMessage] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);

	const { t } = useLanguage();

	useEffect(() => {
		// Fetch people data since admin access is already verified by AdminRoute
		const fetchPeople = async () => {
			try {
				const getPeople = httpsCallable(functions, 'getPeople');
				const result = await getPeople();
				setPeople(result.data.people);
			} catch (error) {
				setMessage(`Error fetching people: ${error.message}`);
			}
		};

		fetchPeople();
	}, []);

	const handleAddPerson = async (e) => {
		e.preventDefault();

		const addPerson = httpsCallable(functions, 'addPerson');
		try {
			const result = await addPerson({ name, surname });
			setMessage(`Person added with ID: ${result.data.id}`);
			setPeople([...people, { id: result.data.id, name, surname }]);
		} catch (error) {
			setMessage(`Error: ${error.message}`);
		}
	};

	const handleSelectPerson = async (personId) => {
		const getResolutions = httpsCallable(functions, 'getResolutions');
		try {
			const result = await getResolutions({ personId });
			setSelectedPerson({ id: personId, ...people.find((person) => person.id === personId) });
			setResolutions(result.data.resolutions);
		} catch (error) {
			setMessage(`Error: ${error.message}`);
		}
	};

	const handleAddResolution = async (e) => {
		e.preventDefault();
		const addResolution = httpsCallable(functions, 'addResolution');
		try {
			const resolution = {
				text: resolutionText,
				count: resolutionCount ? parseInt(resolutionCount) : null,
				year: resolutionYear,
				completedCount: 0,
				expireDate: new Date(resolutionYear, 11, 31, 23, 59, 59).toISOString(),
			};
			const response = await addResolution({ personId: selectedPerson.id, resolution });
			resolution.id = response.data.id;
			setMessage(t('resolutionAddedSuccessfully'));
			setResolutions([...resolutions, resolution]);
		} catch (error) {
			setMessage(`Error: ${error.message}`);
		}
	};

	const handleUpdateResolution = async (resolutionId, updatedResolution) => {
		const updateResolution = httpsCallable(functions, 'updateResolution');
		try {
			await updateResolution({ personId: selectedPerson.id, resolutionId, resolution: updatedResolution });
			setMessage(t('resolutionUpdatedSuccessfully'));
			setResolutions(resolutions.map((res) => (res.id === resolutionId ? updatedResolution : res)));
		} catch (error) {
			setMessage(`Error: ${error.message}`);
		}
	};

	const handleDeleteResolution = async (resolutionId) => {
		const deleteResolution = httpsCallable(functions, 'deleteResolution');
		try {
			await deleteResolution({ personId: selectedPerson.id, resolutionId });
			setMessage('Resolution deleted successfully');
			setResolutions(resolutions.filter((res) => res.id !== resolutionId));
		} catch (error) {
			setMessage(`Error: ${error.message}`);
		}
	};

	const handleSendEmail = async ({ recipients, subject, message }) => {
		try {
			const sendAdminEmail = httpsCallable(functions, 'sendAdminEmail');
			const result = await sendAdminEmail({ recipients, subject, message });
			setMessage(`Email sent to ${result.data.sent} user(s).`);
			return true;
		} catch (error) {
			setMessage(`Error sending email: ${error.message}`);
			return false;
		}
	};

	const currentHost = `${window.location.protocol}//${window.location.hostname}`;

	const getBgClassByStatusCode = (statusCode) => {
		if (statusCode === 'completed') return 'bg-green-400';
		if (statusCode === 'ahead') return 'bg-blue-400';
		if (statusCode === 'behind') return 'bg-red-400';
	};

	const listResolutions = () => {
		if (!selectedPerson || !resolutions) return null;

		// Get year keys as numbers and sort descending
		const years = Object.keys(resolutions)
			.map((k) => Number(k))
			.filter((n) => !Number.isNaN(n))
			.sort((a, b) => b - a);

		// For each year return a header row then its resolution rows
		return years.flatMap((year) => {
			const listForYear = resolutions[year] || [];
			const header = (
				<tr key={`header-${year}`}>
					<th colSpan='5' className='py-2 px-4 border border-gray-300 font-bold bg-gray-200'>
						Year: {year}
					</th>
				</tr>
			);

			const rows = listForYear.map((resolution) => (
				<tr key={resolution.id}>
					<td className='py-2 px-4 border border-gray-300'>
						{resolution.isEncrypted ? '[Private]' : resolution.text}
					</td>
					<td className='py-2 px-4 border border-gray-300'>
						{resolution.count ? `${resolution.completedCount}/${resolution.count}` : 'N/A'}
					</td>
					<td className='py-2 px-4 border border-gray-300'>{`${resolution.progress}%`}</td>
					<td className={`py-2 px-4 border border-gray-300 ${getBgClassByStatusCode(resolution.statusCode)}`}>
						{resolution.status}
					</td>
					<td className='py-2 px-4 border border-gray-300'>
						<button
							onClick={() =>
								handleUpdateResolution(resolution.id, {
									...resolution,
									completedCount: resolution.completedCount + 1,
								})
							}
							className='ml-2 px-2 py-1 bg-green-500 text-white rounded-md text-sm'
						>
							+1
						</button>
						<button
							onClick={() => handleDeleteResolution(resolution.id)}
							className='ml-2 px-2 py-1 bg-red-500 text-white rounded-md text-sm'
						>
							Delete
						</button>
					</td>
				</tr>
			));

			return [header, ...rows];
		});
	};

	return (
		<div className='container mx-auto p-4'>
			{/* Header */}
			<div className='flex justify-between items-center mb-6'>
				<h1 className='text-2xl font-bold'>Admin Dashboard</h1>
				<button
					className='px-4 py-2 bg-purple-600 text-white rounded-md ml-4'
					onClick={() => setIsSendEmailModalOpen(true)}
				>
					Send Email
				</button>
			</div>

			{message && <small className='text-red-600'>{message}</small>}
			<h2 className='text-xl font-bold mb-4'>New Year Resolutions</h2>
			<form onSubmit={handleAddPerson} className='mb-4'>
				<div className='mb-2'>
					<label className='block text-sm font-medium text-gray-700'>Name</label>
					<input
						type='text'
						value={name}
						onChange={(e) => setName(e.target.value)}
						className='mt-1 block border border-gray-300 rounded-md shadow-sm px-2'
						required
					/>
				</div>
				<div className='mb-2'>
					<label className='block text-sm font-medium text-gray-700'>Surname</label>
					<input
						type='text'
						value={surname}
						onChange={(e) => setSurname(e.target.value)}
						className='mt-1 block border border-gray-300 rounded-md shadow-sm px-2'
						required
					/>
				</div>
				<button type='submit' className='px-4 py-2 bg-blue-500 text-white rounded-md'>
					Add Person
				</button>
			</form>

			<h2 className='text-xl font-bold mb-4'>People</h2>
			<table className='min-w-full bg-white border border-gray-300'>
				<thead>
					<tr>
						<th className='py-2 px-4 border border-gray-300'>Name</th>
						<th className='py-2 px-4 border border-gray-300'>Surname</th>
						<th className='py-2 px-4 border border-gray-300'>Actions</th>
						<th className='py-2 px-4 border border-gray-300'>URL</th>
					</tr>
				</thead>
				<tbody>
					{people.map((person) => (
						<tr key={person.id}>
							<td className='py-2 px-4 border border-gray-300'>{person.name}</td>
							<td className='py-2 px-4 border border-gray-300'>{person.surname}</td>
							<td className='py-2 px-4 border border-gray-300'>
								<button
									onClick={() => handleSelectPerson(person.id)}
									className='text-blue-500 underline text-sm'
								>
									View Resolutions
								</button>
							</td>
							<td className='py-2 px-4 border border-gray-300'>
								<a
									href={`${currentHost}/${person.id}`}
									target='_blank'
									className='text-blue-500 underline text-sm'
								>
									Person Page
								</a>
							</td>
						</tr>
					))}
				</tbody>
			</table>
			{selectedPerson && (
				<>
					<button
						onClick={() => setIsModalOpen(true)}
						className='px-3 py-1 bg-green-500 text-white rounded-md text-sm mb-4 mt-5'
					>
						Add Resolution
					</button>
					<table className='min-w-full bg-white'>
						<thead>
							<tr>
								<th className='py-2 px-4 border border-gray-300'>Resolution</th>
								<th className='py-2 px-4 border border-gray-300'>Completed</th>
								<th className='py-2 px-4 border border-gray-300'>Progress</th>
								<th className='py-2 px-4 border border-gray-300'>Status</th>
								<th className='py-2 px-4 border border-gray-300'>Actions</th>
							</tr>
						</thead>
						<tbody>{listResolutions()}</tbody>
					</table>
				</>
			)}
			{isModalOpen && (
				<div className='fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75'>
					<div className='bg-white p-6 rounded-lg shadow-lg'>
						<h2 className='text-xl font-bold mb-4'>Add Resolution</h2>
						<form onSubmit={handleAddResolution} className='mb-4'>
							<div className='mb-2'>
								<label className='block text-sm font-medium text-gray-700'>Resolution Text</label>
								<input
									type='text'
									value={resolutionText}
									onChange={(e) => setResolutionText(e.target.value)}
									className='mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm'
									required
								/>
							</div>
							<div className='mb-2'>
								<label className='block text-sm font-medium text-gray-700'>Count (optional)</label>
								<input
									type='number'
									value={resolutionCount}
									onChange={(e) => setResolutionCount(e.target.value)}
									className='mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm'
								/>
							</div>
							<div className='mb-2'>
								<label className='block text-sm font-medium text-gray-700'>Year</label>
								<input
									type='number'
									value={resolutionYear}
									onChange={(e) => setResolutionYear(e.target.value)}
									className='mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm'
									required
								/>
							</div>
							<button type='submit' className='px-3 py-1 bg-blue-500 text-white rounded-md text-sm'>
								Add Resolution
							</button>
							<button
								onClick={() => setIsModalOpen(false)}
								className='ml-2 px-3 py-1 bg-red-500 text-white rounded-md text-sm'
							>
								Cancel
							</button>
						</form>
					</div>
				</div>
			)}
			<SendEmailModal
				isOpen={isSendEmailModalOpen}
				onClose={() => setIsSendEmailModalOpen(false)}
				people={people}
				onSendEmail={handleSendEmail}
			/>
		</div>
	);
}
